import { Fragment, useEffect, useRef, useState } from "react";
import { Icon } from "./Icon";
import { APP_NAME, DEFAULT_VIEW, NAV } from "../nav";
import wordmark from "../assets/images/logo-white-blue.png";
import mark from "../assets/images/icon-128.png";

export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = e => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

function initials(name) {
  return name
    ? name.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase()
    : "?";
}

function AccountMenu({ account, onSignOut }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const name = account.name || account.username || "Signed in";

  // Outside click and Escape both close; Escape returns focus to the trigger.
  useEffect(() => {
    if (!open) return;
    const onPointer = e => { if (!rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = e => {
      if (e.key === "Escape") { setOpen(false); triggerRef.current.focus(); }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="account" ref={rootRef}>
      <button
        ref={triggerRef}
        className="avatar"
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls="account-menu"
        aria-label={`Account: ${name}`}
        title={name}
        onClick={() => setOpen(o => !o)}
      >
        {initials(account.name)}
      </button>
      <div className="menu" id="account-menu" hidden={!open}>
        <div className="menu__header">
          <div className="menu__name">{name}</div>
          {account.username && <div className="menu__email">{account.username}</div>}
        </div>
        <button className="menu__item" type="button" onClick={onSignOut}>
          <Icon name="logout" size={16} />
          Sign out
        </button>
      </div>
    </div>
  );
}

export function AppBar({ account, onSignOut, navOpen, onMenu, menuButtonRef }) {
  return (
    <header className="appbar">
      {onMenu && (
        <button
          ref={menuButtonRef}
          className="iconbtn appbar__menu"
          type="button"
          aria-label={navOpen ? "Close navigation" : "Open navigation"}
          aria-controls="app-nav"
          aria-expanded={navOpen}
          onClick={onMenu}
        >
          <Icon name="menu" />
        </button>
      )}
      <a className="brand" href={`#/${DEFAULT_VIEW}`} aria-label="Ehrenkranz Partners — home">
        <img className="brand__logo" src={wordmark} alt="Ehrenkranz Partners" />
        <img className="brand__icon" src={mark} alt="Ehrenkranz Partners" />
      </a>
      <span className="appbar__divider" />
      {/* Standalone app: the suite-selector slot carries the app name, not a menu. */}
      <span className="appbar__app">{APP_NAME}</span>
      <div className="appbar__spacer" />
      {account && (
        <div className="appbar__actions">
          <AccountMenu account={account} onSignOut={onSignOut} />
        </div>
      )}
    </header>
  );
}

const allGroupsOpen = () => Object.fromEntries(NAV.map(g => [g.group, true]));

export function SideNav({ current, compact, open, onToggleCompact, onNavigate, footer }) {
  const [expanded, setExpanded] = useState(allGroupsOpen);
  const className = ["sidenav", compact && "sidenav--compact", open && "sidenav--open"]
    .filter(Boolean).join(" ");

  const toggleRail = () => {
    // Entering compact hides the group headers, so expand every group first
    // or a collapsed group's links would be unreachable after returning.
    if (!compact) setExpanded(allGroupsOpen());
    onToggleCompact();
  };

  return (
    <nav className={className} id="app-nav" aria-label={`${APP_NAME} sections`}>
      {NAV.map(({ group, items }) => {
        const listId = `nav-${group.toLowerCase()}`;
        const isOpen = compact || expanded[group];
        return (
          <Fragment key={group}>
            <button
              className="sidenav__toggle"
              type="button"
              aria-expanded={isOpen}
              aria-controls={listId}
              onClick={() => setExpanded(e => ({ ...e, [group]: !e[group] }))}
            >
              <span>{group}</span>
              <Icon name="chev" size={15} className="nav-chevron" />
            </button>
            <div className="sidenav__compact-group" aria-hidden="true" />
            <ul className="sidenav__nested" id={listId} hidden={!isOpen}>
              {items.map(item => (
                <li key={item.id}>
                  <a
                    className="sidenav__link"
                    href={`#/${item.id}`}
                    aria-current={current === item.id ? "page" : undefined}
                    aria-label={item.label}
                    title={item.label}
                    onClick={onNavigate}
                  >
                    <Icon name={item.icon} />
                    <span>{item.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </Fragment>
        );
      })}

      <div className="sidenav__foot">
        <p className="muted sidenav__note">{footer}</p>
        <button
          className="sidenav__rail-toggle"
          type="button"
          aria-expanded={!compact}
          aria-label={compact ? "Expand navigation" : "Collapse navigation"}
          title={compact ? "Expand navigation" : "Collapse navigation"}
          onClick={toggleRail}
        >
          <Icon name="chev" size={18} />
        </button>
      </div>
    </nav>
  );
}
